// HarvestHeroes.jsx
// Solo, responsive, front-end only "Harvest Heroes" seasonal competition game for farmers.
// Features:
// - Seasonal events (Spring, Summer, Monsoon, Winter) that change crop sets and bonuses
// - Timed challenges (3 challenge types: Speed Harvest, Watering Rhythm, Pest Defense)
// - Combo scoring, streaks, and medals
// - Local leaderboard saved in localStorage (solo local competition)
// - Responsive layout suitable for mobile and desktop
// - All logic client-side; import into your React app as `import HarvestHeroes from './components/HarvestHeroes'`

import React, { useEffect, useRef, useState } from 'react';

const SEASONS = ['Spring', 'Summer', 'Monsoon', 'Winter'];
const DEFAULT_SEASON = 0; // Spring
const CHALLENGE_TIME_MS = 20000; // 20s default challenge time
const INITIAL_COINS = 200;

const CROP_SETS = {
  Spring: [{name:'Wheat', icon:'🌾', baseScore:10},{name:'Pea', icon:'🟩', baseScore:8}],
  Summer: [{name:'Corn', icon:'🌽', baseScore:12},{name:'Tomato', icon:'🍅', baseScore:11}],
  Monsoon: [{name:'Rice', icon:'🍚', baseScore:9},{name:'Sugarcane', icon:'🎋', baseScore:10}],
  Winter: [{name:'Carrot', icon:'🥕', baseScore:9},{name:'Potato', icon:'🥔', baseScore:8}]
};

function randInt(min, max){ return Math.floor(Math.random() * (max - min + 1)) + min; }

function loadLS(key, fallback){ try{ const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch(e){ return fallback; } }
function saveLS(key, value){ try{ localStorage.setItem(key, JSON.stringify(value)); } catch(e){} }

export default function HarvestHeroes(){
  const [seasonIndex, setSeasonIndex] = useState(() => loadLS('hh_season', DEFAULT_SEASON));
  const seasonName = SEASONS[seasonIndex % SEASONS.length];
  const [coins, setCoins] = useState(() => loadLS('hh_coins', INITIAL_COINS));
  const [medals, setMedals] = useState(() => loadLS('hh_medals', {gold:0,silver:0,bronze:0}));
  const [leaderboard, setLeaderboard] = useState(() => loadLS('hh_leaderboard', []));
  const [activeChallenge, setActiveChallenge] = useState(null); // {type, startTs, timeMs, data, running}
  const [message, setMessage] = useState('Welcome to Harvest Heroes — seasonal crop competitions!');
  const [streak, setStreak] = useState(() => loadLS('hh_streak', 0));
  const timerRef = useRef(null);
  const [scoreThisRound, setScoreThisRound] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  // persist
  useEffect(()=> saveLS('hh_season', seasonIndex), [seasonIndex]);
  useEffect(()=> saveLS('hh_coins', coins), [coins]);
  useEffect(()=> saveLS('hh_medals', medals), [medals]);
  useEffect(()=> saveLS('hh_leaderboard', leaderboard), [leaderboard]);
  useEffect(()=> saveLS('hh_streak', streak), [streak]);

  // helper: start a challenge
  function startChallenge(type) {
  setActiveChallenge(prev => {
    if (prev && prev.running) {
      setMessage('Finish current challenge first.');
      return prev; // do nothing if already running
    }

    const timeMs = CHALLENGE_TIME_MS;
    const now = Date.now();
    const data = generateChallengeData(type, seasonName);

    // new challenge
    const challenge = { type, startTs: now, timeMs, data, running: true };
    setScoreThisRound(0);
    setMessage(`Challenge started: ${type}. Good luck!`);

    // clear any existing timer
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => finishChallenge(false), timeMs + 50);

    return challenge;
  });
}


  function generateChallengeData(type, season){
    const crops = CROP_SETS[season];
    if(type === 'Speed Harvest'){
      // array of targets to tap
      const targets = Array.from({length: randInt(6,10) }).map(()=> ({ crop: crops[randInt(0,crops.length-1)], id: Math.random().toString(36).slice(2,8) }));
      return { targets, tapped: [] };
    }
    if(type === 'Watering Rhythm'){
      // sequence of length 5-8 to follow
      const seqLen = randInt(4,7);
      const seq = Array.from({length:seqLen}).map(()=> crops[randInt(0,crops.length-1)]);
      return { seq, pos:0, score:0 };
    }
    if(type === 'Pest Defense'){
      // pests appear randomly on 8 slots, tap to spray
      const slots = 8; const pests = Array.from({length:slots}).map(()=> ({id:Math.random().toString(36).slice(2,8), active:false}));
      return { slots: pests, spawnRate: 800, activeCount:0, hits:0, misses:0 };
    }
    return {};
  }

  // actions for Speed Harvest
  function tapTarget(id){
    if(!activeChallenge || !activeChallenge.running) return;
    if(activeChallenge.type !== 'Speed Harvest') return;
    const found = activeChallenge.data.targets.find(t=>t.id===id);
    if(!found) return;
    // remove target
    const newTargets = activeChallenge.data.targets.filter(t=>t.id!==id);
    const gained = 20 + Math.floor(Math.random()*10) + (streak*2);
    setScoreThisRound(s=> s + gained);
    setMessage(`Good harvest! +${gained} pts`);
    setStreak(st=> st+1);
    setActiveChallenge(ac => ({...ac, data:{...ac.data, targets:newTargets}}));
  }

  // actions for Watering Rhythm: user presses a crop to match next in sequence
  function pressCropForSequence(idx){
    if(!activeChallenge || !activeChallenge.running) return;
    if(activeChallenge.type !== 'Watering Rhythm') return;
    const seq = activeChallenge.data.seq;
    const expected = seq[activeChallenge.data.pos];
    const chosen = seq[idx % seq.length]; // mapping UI to crop index
    const correct = chosen.name === expected.name;
    if(correct){
      const add = 30 + Math.floor(Math.random()*10) + (streak*3);
      setScoreThisRound(s=> s + add);
      setMessage(`Nice rhythm! +${add} pts`);
      setActiveChallenge(ac => ({...ac, data:{...ac.data, pos: ac.data.pos+1}}));
      setStreak(st=> st+1);
    } else {
      setMessage('Mismatch — keep practicing the rhythm.');
      setStreak(0);
      setActiveChallenge(ac => ({...ac, data:{...ac.data, pos: Math.max(0, ac.data.pos-0)}}));
    }
  }

  // Pest Defense: spawn loop
  useEffect(()=>{
    let spawnTimer = null;
    if(activeChallenge && activeChallenge.running && activeChallenge.type === 'Pest Defense'){
      function spawn(){
        setActiveChallenge(ac => {
          if(!ac) return ac;
          const data = {...ac.data};
          const emptySlots = data.slots.filter(s=>!s.active);
          if(emptySlots.length>0 && Math.random() < 0.65){
            const idx = data.slots.findIndex(s=>!s.active);
            if(idx>=0){ data.slots[idx].active = true; data.activeCount = (data.activeCount||0)+1; }
          }
          return {...ac, data};
        });
        spawnTimer = setTimeout(spawn, activeChallenge.data.spawnRate || 900);
      }
      spawnTimer = setTimeout(spawn, 400);
    }
    return ()=> { if(spawnTimer) clearTimeout(spawnTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChallenge && activeChallenge.running]);

  function sprayPest(slotId){
    if(!activeChallenge || !activeChallenge.running) return;
    if(activeChallenge.type !== 'Pest Defense') return;
    let hit = false;
    setActiveChallenge(ac => {
      const data = {...ac.data};
      const idx = data.slots.findIndex(s=>s.id===slotId);
      if(idx>=0 && data.slots[idx].active){ data.slots[idx].active = false; data.hits = (data.hits||0)+1; hit=true; }
      else { data.misses = (data.misses||0)+1; }
      return {...ac, data};
    });
    if(hit){ setScoreThisRound(s=> s + 25 + Math.floor(Math.random()*8) + (streak*2)); setMessage('Pest sprayed — good defense!'); setStreak(st=> st+1); }
    else { setMessage('Miss — nothing there.'); setStreak(0); }
  }

  // finish challenge. success param indicates timely submit early/finish
  function finishChallenge(early=true){
    if(!activeChallenge) return;
    const ac = activeChallenge;
    // compute final score with time bonus
    let base = scoreThisRound;
    const timeLeft = Math.max(0, (ac.startTs + ac.timeMs) - Date.now());
    const timeBonus = Math.round(timeLeft / 1000) * 5;
    const total = base + timeBonus + (streak*2);

    // medal thresholds flexible
    const goldThresh = 400; const silverThresh = 220; const bronzeThresh = 120;
    let medalEarned = null;
    if(total >= goldThresh){ medalEarned = 'gold'; setMedals(m=> ({...m, gold: m.gold+1})); setMessage('Gold Medal! Amazing harvest!'); setCoins(c=> c + 150); }
    else if(total >= silverThresh){ medalEarned = 'silver'; setMedals(m=> ({...m, silver: m.silver+1})); setMessage('Silver Medal — great job!'); setCoins(c=> c + 90); }
    else if(total >= bronzeThresh){ medalEarned = 'bronze'; setMedals(m=> ({...m, bronze: m.bronze+1})); setMessage('Bronze Medal — good effort!'); setCoins(c=> c + 40); }
    else { setMessage('Challenge complete — keep practicing to earn medals.'); setCoins(c=> c + 10); setStreak(0); }

    // record on leaderboard (local)
    const entry = { ts: Date.now(), season: seasonName, challenge: ac.type, score: total, medal: medalEarned };
    setLeaderboard(lb => { const nb = [entry, ...lb].slice(0,20); return nb; });

    // cleanup
    setActiveChallenge(null);
    setScoreThisRound(0);
    if(timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  }

  function cancelChallenge(){ if(activeChallenge){ setActiveChallenge(null); setMessage('Challenge cancelled.'); if(timerRef.current){ clearTimeout(timerRef.current); timerRef.current=null; } } }

  function changeSeason(dir){ setSeasonIndex(idx=> (idx + dir + SEASONS.length) % SEASONS.length); setMessage('Season changed — crops updated.'); }

  function resetGame(){ if(!window.confirm('Reset Harvest Heroes?')) return; localStorage.removeItem('hh_season'); localStorage.removeItem('hh_coins'); localStorage.removeItem('hh_medals'); localStorage.removeItem('hh_leaderboard'); localStorage.removeItem('hh_streak'); window.location.reload(); }

  // small UI helpers
  const crops = CROP_SETS[seasonName];

  return (
    <div style={{padding:16, maxWidth:1100, margin:'0 auto', fontFamily:'system-ui,Segoe UI,Roboto,Helvetica,Arial'}}>
      <style>{`
        .top{display:flex; gap:12px; align-items:center; justify-content:space-between; flex-wrap:wrap}
        .panel{background:#fff; padding:10px; border-radius:10px; box-shadow:0 6px 18px rgba(0,0,0,0.06)}
        .grid{display:grid; grid-template-columns:1fr 320px; gap:12px}
        .controls{display:flex; gap:8px; flex-wrap:wrap}
        .crop-row{display:flex; gap:8px; align-items:center}
        .targets{display:grid; grid-template-columns:repeat(auto-fit, minmax(70px,1fr)); gap:8px}
        .slot{padding:6px; border-radius:8px; text-align:center; cursor:pointer}
        .pest-slot{height:56px; display:flex; align-items:center; justify-content:center}
        @media(max-width:880px){ .grid{grid-template-columns:1fr} }
      `}</style>

      <div className="top">
        <div style={{display:'flex', gap:12, alignItems:'center'}}>
          <h2 style={{margin:0}}>🏆 Harvest Heroes</h2>
          <div className='text-white'>Season: <strong>{seasonName}</strong></div>
          <div className='text-white'>Coins: <strong>₹{coins}</strong></div>
          <div className='text-white'>Streak: <strong>{streak}</strong></div>
        </div>

        <div className='flex flex-row gap-2'>
          <button onClick={() => changeSeason(-1)}>Prev Season</button>
          <button onClick={() => changeSeason(1)}>Next Season</button>
          <button onClick={() => setIsMuted(m=>!m)}>{isMuted? 'Unmute':'Mute'}</button>
          <button onClick={resetGame}>Reset</button>
        </div>
      </div>

      <div className="grid" style={{marginTop:12}}>
        <div>
          <div className="panel">
            <h3 className="text-blue-700" style={{marginTop:0}}>Choose Challenge</h3>
            <div className='flex flex-row gap-2'>
              <button onClick={() => startChallenge('Speed Harvest')}>Speed Harvest</button>
              <button onClick={() => startChallenge('Watering Rhythm')}>Watering Rhythm</button>
              <button onClick={() => startChallenge('Pest Defense')}>Pest Defense</button>
            </div>

            <div style={{marginTop:12}}>
              <h4 className="text-blue-700" style={{margin:'6px 0'}}>Challenge Area</h4>
              {activeChallenge ? (
                <div>
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <div><strong>{activeChallenge.type}</strong></div>
                    <div className="panel">Time left: <strong>{Math.max(0, Math.ceil(((activeChallenge.startTs + activeChallenge.timeMs) - Date.now())/1000))}s</strong></div>
                  </div>

                  {/* Speed Harvest UI */}
                  {activeChallenge.type === 'Speed Harvest' && (
                    <div style={{marginTop:10}}>
                      <div className="targets">
                        {activeChallenge.data.targets.length === 0 ? <div className="panel">All harvested — finish to score!</div> : activeChallenge.data.targets.map(t => (
                          <div key={t.id} className="slot panel" onClick={() => tapTarget(t.id)}>
                            <div style={{fontSize:28}}>{t.crop.icon}</div>
                            <div style={{fontWeight:600}}>{t.crop.name}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Watering Rhythm UI */}
                  {activeChallenge.type === 'Watering Rhythm' && (
                    <div style={{marginTop:10}}>
                      <div style={{display:'flex', gap:8, justifyContent:'center', marginBottom:8}}>
                        {activeChallenge.data.seq.map((c,i)=> (
                          <div key={i} className="panel" style={{padding:'8px 10px', opacity:i < activeChallenge.data.pos ? 0.6 : 1}}>{c.icon} <div style={{fontSize:12}}>{i===activeChallenge.data.pos? 'Next':''}</div></div>
                        ))}
                      </div>
                      <div className="crop-row">
                        {crops.map((c,idx) => (
                          <button key={c.name} onClick={() => pressCropForSequence(idx)} style={{minWidth:88}}>{c.icon}<div style={{fontSize:12}}>{c.name}</div></button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pest Defense UI */}
                  {activeChallenge.type === 'Pest Defense' && (
                    <div style={{marginTop:10}}>
                      <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8}}>
                        {activeChallenge.data.slots.map(s => (
                          <div key={s.id} className={`panel pest-slot`} onClick={() => sprayPest(s.id)} style={{background: s.active? '#ffecec' : '#f7fff7'}}>
                            {s.active ? '🐛' : '🌾'}
                          </div>
                        ))}
                      </div>
                      <div style={{marginTop:8}}>Hits: {activeChallenge.data.hits || 0} • Misses: {activeChallenge.data.misses || 0}</div>
                    </div>
                  )}

                  <div style={{display:'flex', gap:8, marginTop:12}}>
                    <button onClick={() => finishChallenge(true)}>Finish Challenge</button>
                    <button onClick={cancelChallenge}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="panel">No active challenge. Pick one above and test your farming skills!</div>
              )}

            </div>

          </div>

          <div style={{marginTop:12}} className="panel">
            <h4 style={{marginTop:0}}>Round Score: <strong>{scoreThisRound}</strong></h4>
            <div>{message}</div>
          </div>

        </div>

        <aside>
          <div className="panel">
            <h4 style={{marginTop:0}}>Season Crops</h4>
            <div style={{display:'flex', gap:8}}>
              {crops.map(c => (
                <div key={c.name} className="panel" style={{textAlign:'center'}}>
                  <div style={{fontSize:28}}>{c.icon}</div>
                  <div style={{fontWeight:700}}>{c.name}</div>
                  <div style={{fontSize:12}}>Base: {c.baseScore}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="panel" style={{marginTop:12}}>
            <h4 style={{marginTop:0}}>Medals</h4>
            <div>🥇 {medals.gold} • 🥈 {medals.silver} • 🥉 {medals.bronze}</div>
            <div style={{marginTop:8}}>
              <button onClick={() => { if(coins >= 100){ setCoins(c=>c-100); setMedals(m=> ({...m, bronze: m.bronze+1})); setMessage('Purchased Bronze medal for showcase.'); } else setMessage('Not enough coins.'); }}>Buy Bronze (₹100)</button>
            </div>
          </div>

          <div className="panel" style={{marginTop:12}}>
            <h4 style={{marginTop:0}}>Local Leaderboard</h4>
            {leaderboard.length===0 ? <div style={{color:'#666'}}>No plays yet</div> : (
              <ol style={{paddingLeft:16}}>
                {leaderboard.map((e,idx) => (
                  <li key={idx} style={{marginBottom:6}}>{new Date(e.ts).toLocaleString()} — {e.challenge} — {e.score}{e.medal? ` • ${e.medal.toUpperCase()}` : ''}</li>
                ))}
              </ol>
            )}
          </div>

        </aside>
      </div>

      <footer className='bg-gary-100 mt-12'>Harvest Heroes — seasonal solo competitions. Want online leaderboard or shareable scores? I can add server sync later.</footer>
    </div>
  );
}
